import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { BLOG } from "../lib/siteData";
import { Calendar, User, ArrowRight, ArrowLeft } from "lucide-react";
import CTABanner from "../components/CTABanner";
import SEO from "../components/SEO";

export function BlogListPage() {
  return (
    <div data-testid="blog-list-page">
      <SEO title="Blog" description="Solar energy insights, government subsidy guides, panel buying advice and commercial solar case studies from CNGTPL Solar." />
      <section className="py-16 lg:py-20 bg-gradient-to-br from-slate-900 to-blue-900 text-white">
        <div className="container-narrow">
          <div className="badge-eco bg-blue-600/20 border-blue-600/40 text-blue-300 mb-3">Insights & Guides</div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tight">Solar Blog</h1>
          <p className="text-slate-300 mt-3 max-w-2xl">Actionable guides on solar energy, subsidy, panel selection, and commercial ROI — written by our expert team.</p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-narrow grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {BLOG.map((b) => (
            <Link key={b.slug} to={`/blog/${b.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition-all" data-testid={`blog-card-${b.slug}`}>
              <div className="aspect-video overflow-hidden bg-slate-100">
                <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" loading="lazy" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 font-semibold uppercase tracking-widest">{b.category}</span>
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {b.date}</span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2 group-hover:text-blue-800 transition">{b.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-3 line-clamp-3">{b.excerpt}</p>
                <div className="flex items-center text-blue-700 text-sm font-semibold gap-1">Read article <ArrowRight className="w-4 h-4" /></div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <CTABanner />
    </div>
  );
}

export function BlogDetailPage() {
  const { slug } = useParams();
  const post = BLOG.find((b) => b.slug === slug);
  if (!post) return <Navigate to="/blog" />;

  return (
    <div data-testid={`blog-detail-${slug}`}>
      <SEO title={post.title} description={post.excerpt} />
      <article className="section-pad">
        <div className="container-narrow max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-blue-700 hover:text-blue-800 mb-5"><ArrowLeft className="w-4 h-4" /> All articles</Link>
          <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
            <span className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 font-semibold uppercase tracking-widest">{post.category}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.date}</span>
            <span className="flex items-center gap-1"><User className="w-3 h-3" /> {post.author}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-slate-900 tracking-tight leading-tight mb-6">{post.title}</h1>
          <div className="aspect-video rounded-2xl overflow-hidden mb-8 bg-slate-100">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>
          <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-line text-base">
            {post.content}
          </div>
        </div>
      </article>
      <CTABanner />
    </div>
  );
}
